import type { TensorDType, TensorLayout } from '@design-ai/gpu-core';

export type ModelTask = 'matting' | 'upscale';

/** Whether the weights may be used in a commercial POD workflow. */
export type CommercialUse = 'yes' | 'no' | 'check-license';

export interface ModelIO {
  /**
   * Fixed square input edge the network expects, or `'dynamic'` when it accepts
   * any size. Dynamic models are still fed a fixed tile size by the pipeline -
   * ORT recompiles its shaders per shape, so varying it per tile is far slower
   * than padding to a constant.
   */
  size: number | 'dynamic';
  /** Input size must be a multiple of this (window-based transformers need 8). */
  sizeMultiple?: number;
  channels: number;
  layout: TensorLayout;
  dtype: TensorDType;
  /** Per-channel input normalisation: `(v - mean) / std`, v in [0, 1]. */
  mean: readonly number[];
  std: readonly number[];
  /** Channels the model returns: 1 for a matte, 3 for an image. */
  outputChannels: number;
  /** Resolution multiplier for super-resolution models. */
  outputScale: number;
  /** De-normalisation applied to the output: `v * scale + bias`. */
  outputDenorm?: { scale: readonly number[]; bias: readonly number[] };
}

export interface ModelEntry {
  id: string;
  task: ModelTask;
  label: string;
  /** Direct download URL for the `.onnx` file. */
  url: string;
  /** Approximate download size, for the progress UI. */
  approxBytes?: number;
  license: string;
  commercialUse: CommercialUse;
  /** Shown in the model picker. */
  notes?: string;
  io: ModelIO;
}

/**
 * Built-in model catalogue.
 *
 * Deliberately data-driven and overridable: model repositories rename files, and
 * a POD workflow may need weights whose licence suits commercial resale. The
 * workbench can add an entry from a URL or a local `.onnx` file at runtime, so
 * nothing here is load-bearing.
 *
 * Licence column matters more than usual for this project. RMBG-1.4 is the
 * highest quality option for garment mockups but BRIA licenses it for
 * non-commercial use unless you buy a commercial licence - shipping products cut
 * out with it is a licensing decision, not a technical one.
 */
export const MODEL_REGISTRY: ModelEntry[] = [
  {
    id: 'rmbg-1.4-fp16',
    task: 'matting',
    label: 'RMBG-1.4 (fp16)',
    url: 'https://huggingface.co/briaai/RMBG-1.4/resolve/main/onnx/model_fp16.onnx',
    approxBytes: 88_217_533,
    license: 'bria-rail-m (non-commercial without a paid licence)',
    commercialUse: 'no',
    notes: 'Best quality on garment mockups. ~88 MB, 1024x1024 fixed input.',
    io: {
      size: 1024,
      channels: 3,
      layout: 'nchw',
      dtype: 'float16',
      mean: [0.5, 0.5, 0.5],
      std: [1, 1, 1],
      outputChannels: 1,
      outputScale: 1,
    },
  },
  {
    id: 'rmbg-1.4-fp32',
    task: 'matting',
    label: 'RMBG-1.4 (fp32)',
    url: 'https://huggingface.co/briaai/RMBG-1.4/resolve/main/onnx/model.onnx',
    approxBytes: 176_153_355,
    license: 'bria-rail-m (non-commercial without a paid licence)',
    commercialUse: 'no',
    notes: 'Same weights at full precision; use when fp16 shows banding.',
    io: {
      size: 1024,
      channels: 3,
      layout: 'nchw',
      dtype: 'float32',
      mean: [0.5, 0.5, 0.5],
      std: [1, 1, 1],
      outputChannels: 1,
      outputScale: 1,
    },
  },
  {
    id: 'rmbg-1.4-quantized',
    task: 'matting',
    label: 'RMBG-1.4 (int8)',
    url: 'https://huggingface.co/briaai/RMBG-1.4/resolve/main/onnx/model_quantized.onnx',
    approxBytes: 44_403_226,
    license: 'bria-rail-m (non-commercial without a paid licence)',
    commercialUse: 'no',
    notes: 'Smallest download. Edges are noticeably coarser; lean on the guided filter.',
    io: {
      size: 1024,
      channels: 3,
      layout: 'nchw',
      dtype: 'float32',
      mean: [0.5, 0.5, 0.5],
      std: [1, 1, 1],
      outputChannels: 1,
      outputScale: 1,
    },
  },
  {
    id: 'swin2sr-x2',
    task: 'upscale',
    label: 'Swin2SR classical x2',
    url: 'https://huggingface.co/Xenova/swin2SR-classical-sr-x2-64/resolve/main/onnx/model.onnx',
    approxBytes: 54_428_699,
    license: 'apache-2.0',
    commercialUse: 'yes',
    notes: 'Gentle 2x. Window size 8, so tiles are padded to a multiple of 8.',
    io: {
      size: 'dynamic',
      sizeMultiple: 8,
      channels: 3,
      layout: 'nchw',
      dtype: 'float32',
      mean: [0, 0, 0],
      std: [1, 1, 1],
      outputChannels: 3,
      outputScale: 2,
    },
  },
  {
    id: 'swin2sr-realworld-x4',
    task: 'upscale',
    label: 'Swin2SR real-world x4',
    url: 'https://huggingface.co/Xenova/swin2SR-realworld-sr-x4-64-bsrgan-psnr/resolve/main/onnx/model.onnx',
    approxBytes: 52_772_645,
    license: 'apache-2.0',
    commercialUse: 'yes',
    notes: 'Trained on degraded inputs - the right choice for marketplace screenshots.',
    io: {
      size: 'dynamic',
      sizeMultiple: 8,
      channels: 3,
      layout: 'nchw',
      dtype: 'float32',
      mean: [0, 0, 0],
      std: [1, 1, 1],
      outputChannels: 3,
      outputScale: 4,
    },
  },
  {
    id: 'swin2sr-compressed-x4',
    task: 'upscale',
    label: 'Swin2SR compressed x4',
    url: 'https://huggingface.co/Xenova/swin2SR-compressed-sr-x4-48/resolve/main/onnx/model.onnx',
    approxBytes: 55_046_351,
    license: 'apache-2.0',
    commercialUse: 'yes',
    notes: 'Tuned for JPEG artefacts, which every Amazon/Etsy screenshot has.',
    io: {
      size: 'dynamic',
      sizeMultiple: 8,
      channels: 3,
      layout: 'nchw',
      dtype: 'float32',
      mean: [0, 0, 0],
      std: [1, 1, 1],
      outputChannels: 3,
      outputScale: 4,
    },
  },
];

/**
 * Why a model cannot run on this device, or `null` if it can.
 *
 * fp16 weights need `shader-f16`: ORT compiles f16 arithmetic into its own
 * kernels and refuses at run time without the feature ("Program Mul requires f16
 * but the device does not support it"). Note this is about the *model*, not our
 * tensor packing - `packTensor` uses `pack2x16float` and needs no feature at all,
 * so an fp16 tensor round trip succeeds on hardware where an fp16 model will not
 * run.
 */
export function modelUnsupportedReason(
  entry: ModelEntry,
  caps: { shaderF16: boolean },
): string | null {
  if (entry.io.dtype === 'float16' && !caps.shaderF16) {
    return 'This GPU does not support shader-f16; pick the fp32 build instead.';
  }
  return null;
}

export function findModel(id: string): ModelEntry | undefined {
  return MODEL_REGISTRY.find((entry) => entry.id === id);
}

export function modelsForTask(task: ModelTask): ModelEntry[] {
  return MODEL_REGISTRY.filter((entry) => entry.task === task);
}

/**
 * Builds an entry for a model the user supplied themselves.
 *
 * Real-ESRGAN and its anime variants are the strongest upscalers for flat vector
 * artwork but have no canonical ONNX host, so the workbench takes a URL or a
 * local file and pairs it with the IO description here.
 */
export function customModel(
  id: string,
  task: ModelTask,
  url: string,
  io: ModelIO,
  overrides: Partial<ModelEntry> = {},
): ModelEntry {
  return {
    id,
    task,
    label: overrides.label ?? id,
    url,
    license: overrides.license ?? 'user-supplied',
    commercialUse: overrides.commercialUse ?? 'check-license',
    io,
    ...overrides,
  };
}

/** IO description matching the Real-ESRGAN family (fully convolutional, x4). */
export const REAL_ESRGAN_X4_IO: ModelIO = {
  size: 'dynamic',
  sizeMultiple: 4,
  channels: 3,
  layout: 'nchw',
  dtype: 'float32',
  mean: [0, 0, 0],
  std: [1, 1, 1],
  outputChannels: 3,
  outputScale: 4,
};
